import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';

let initialised = false;
let activePixelId = '';
let pendingUserData = null;
let appliedUserKey = '';

/**
 * Manual Advanced Matching (Phase 80).
 * Normalises the logged-in user's identifiers for `fbq('init', id, data)`:
 * fbq hashes them client-side (SHA-256) before anything leaves the browser.
 *  - em: lowercase, trimmed
 *  - external_id: our user id
 *  - ph: digits only (only when a phone exists on the profile)
 */
const normalizeUserData = (user) => {
  const email = (user?.email || '').trim().toLowerCase();
  if (!email) return null;
  const data = { em: email };
  if (user?.id) data.external_id = String(user.id);
  const digits = String(user?.phone || user?.phone_number || '').replace(/\D/g, '');
  if (digits.length >= 8) data.ph = digits;
  return data;
};

const keyOf = (d) => `${d.em}|${d.external_id || ''}|${d.ph || ''}`;

const applyUserData = (data) => {
  if (typeof window === 'undefined' || !window.fbq || !activePixelId || !data) return;
  const key = keyOf(data);
  if (appliedUserKey === key) return; // already applied for this user
  appliedUserKey = key;
  try {
    // Re-init with the same pixel id + user data: the documented SPA
    // pattern to attach Advanced Matching after login/registration.
    window.fbq('init', activePixelId, data);
  } catch (_e) { /* ignore */ }
};

/**
 * Attach the logged-in user's data to every subsequent browser event
 * (PageView included). Safe to call before the pixel loads: the data is
 * kept pending and applied at install time.
 */
export const setPixelUserData = (user) => {
  const data = normalizeUserData(user);
  if (!data) return;
  if (initialised) applyUserData(data);
  else pendingUserData = data;
};

const installPixel = (pixelId) => {
  if (initialised || !pixelId || typeof window === 'undefined') return;
  initialised = true;

  // Standard Meta Pixel boilerplate (fbq init + PageView)
  /* eslint-disable */
  !function(f,b,e,v,n,t,s){
    if(f.fbq) return;
    n = f.fbq = function(){ n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
    if(!f._fbq) f._fbq = n;
    n.push = n; n.loaded = !0; n.version = '2.0'; n.queue = [];
    t = b.createElement(e); t.async = !0; t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  }(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */

  activePixelId = pixelId;
  if (pendingUserData) {
    // User already known at bootstrap (restored session): init directly
    // with Advanced Matching so even the very first PageView carries it.
    appliedUserKey = keyOf(pendingUserData);
    window.fbq('init', pixelId, pendingUserData);
    pendingUserData = null;
  } else {
    window.fbq('init', pixelId);
  }
  window.fbq('track', 'PageView');
};

export const useMetaPixel = () => {
  const [pixelId, setPixelId] = useState('');

  useEffect(() => {
    let cancelled = false;
    api
      .get('/public/config')
      .then((res) => {
        if (cancelled) return;
        const id = res?.data?.meta_pixel_id || '';
        const enabled = !!res?.data?.meta_enabled;
        if (enabled && id) {
          installPixel(id);
          setPixelId(id);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  return { pixelId };
};

/**
 * Fire a standard Meta Pixel event from the client side. Safe to call even
 * when the pixel isn't loaded — the call simply no-ops.
 *
 * @param event   Standard Meta event name (`PageView`, `CompleteRegistration`,
 *                `Login`, `Lead`, `ViewContent`, `InitiateCheckout`, `Purchase`).
 * @param params  Event-specific params (value, currency, content_ids, etc.).
 * @param eventID Optional deterministic ID that matches the server-side
 *                CAPI `event_id`. Enables Meta to collapse the two events
 *                (browser + server) into one — the recommended pattern.
 */
export const trackPixelEvent = (event, params = {}, eventID = undefined) => {
  if (typeof window === 'undefined' || !window.fbq) return;
  try {
    if (eventID) {
      window.fbq('track', event, params, { eventID });
    } else {
      window.fbq('track', event, params);
    }
  } catch (_e) { /* ignore */ }
};

/**
 * PixelAdvancedMatching — keeps the pixel's Advanced Matching in sync with
 * the auth state. Fires on session restore AND on login/registration
 * completed mid-session (the `user` object appears → re-init with em/
 * external_id/ph). Anonymous visitors send nothing, by design.
 * Mounted once by `App.js` (inside AuthProvider).
 */
export const PixelAdvancedMatching = () => {
  const { user } = useAuth();
  useEffect(() => {
    if (user?.email) setPixelUserData(user);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.email, user?.phone]);
  return null;
};

/**
 * Route-change PageView tracker for the React SPA.
 *
 * The initial `fbq('init', ..., 'PageView')` above fires only ONCE at
 * bootstrap. Because Nymia is a single-page app, subsequent client-side
 * navigations do not reload the page — so Meta would otherwise see one
 * PageView per session instead of one per screen. This component listens
 * to react-router `useLocation()` and emits a fresh PageView on every
 * pathname change (excluding the very first render, which the init
 * boilerplate already covered).
 *
 * Mounted once by `App.js` alongside `MetaPixelLoader`.
 */
export const PixelRouteTracker = () => {
  const location = useLocation();
  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Skip the very first mount — init() already fired PageView.
    if (window.__nymia_last_pageview_path === undefined) {
      window.__nymia_last_pageview_path = location.pathname;
      return;
    }
    if (window.__nymia_last_pageview_path === location.pathname) return;
    // Pixel not installed yet: keep the marker so this PV fires when ready.
    if (!window.fbq) return;
    window.__nymia_last_pageview_path = location.pathname;
    try {
      window.fbq('track', 'PageView', { page_path: location.pathname });
    } catch (_e) { /* ignore */ }
  }, [location.pathname]);
  return null;
};
